<template>
    <div class="search-results">
        <div class="intro">
            <h1>Search the collection</h1>
            <div class="intro-copy">
                <p>
                    Use the drop down menus to search artworks from the entire
                    collection by size range. Select artists from the menu to search
                    and sort works by specific artists.
                </p>
            </div>
            <div class="clear-search">
                <a @click="clearSearch"
                   class="button clear-search"
                   :disabled="isDefaultSearch ? 'disabled' : null"
                >
                    Clear Search Parameters
                    <span class="material-icons">close</span>
                </a>
                <template v-if="!isDefaultSearch">
                    {{ resultCount }} results found
                </template>
                <template v-else>
                    &nbsp;
                </template>
            </div>
        </div>
        <div class="filters">
            <span class="material-icons">search</span>
            <h3>Search By</h3>
            <div class="artist-input">
                <VueMultiselect
                    v-model="searches.artist"
                    :options="alphabeticalArtists.map((a) => a.uid)"
                    :customLabel="
                        (uid) =>
                            `${
                                artists.find((artist) => artist.uid === uid)
                                    .name
                            }`
                    "
                    selectLabel=""
                    deselectLabel=""
                    selectedLabel="[x]"
                    :searchable="false"
                    multiple
                    ><!-- placeholder="All Artists" -->
                    <template v-slot:placeholder><span /></template>
                    <template v-slot:selection="{ values }">
                        <span
                            class="multiselect__single"
                            v-if="values.length == 0"
                        >
                            All Artists
                        </span>
                        <span
                            class="multiselect__single"
                            v-if="values.length == 1"
                        >
                            {{
                                artists.find(
                                    (artist) => artist.uid === values[0]
                                ).name
                            }}
                        </span>
                        <span
                            class="multiselect__single"
                            v-else-if="values.length > 1"
                            ><!-- && !isOpen -->
                            {{ values.length }} artists selected
                        </span>
                    </template>
                </VueMultiselect>
                <span
                    class="clear-artists material-icons"
                    v-if="searches.artist.length"
                    @click="searches.artist = []"
                    >clear</span
                >
            </div>

            <div class="flex-br" />

            <Range
                label="Height"
                classes="height"
                shortLabel="H"
                suffix='"'
                v-model="searches.heightRange"
                :uniqValues="uniqHeights"
            />
            <div class="flex-br flex-br-ranges" />
            <Range
                label="Width"
                shortLabel="W"
                suffix='"'
                v-model="searches.widthRange"
                :uniqValues="uniqWidths"
            />
            <div class="flex-br flex-br-ranges" />
            <Range
                label="Price"
                classes="price"
                prefix="$"
                v-model="searches.priceRange"
                :uniqValues="uniqPrices"
            />

            <div class="flex-br flex-br-keyword" />

            <div class="keyword-input">
                <input
                    type="search"
                    placeholder="keyword, e.g., title, etc."
                    v-model="searches.keyword"
                />
            </div>

            <div class="flex-br" />

            <div class="sort">
                <span class="material-icons">sort</span>
                <h3>Sort By</h3>
                <div class="select-wrap">
                    <select v-model="searches.sortBy">
                        <option value="">Default</option>
                        <option value="size_asc">size ⇧</option>
                        <option value="size_desc">size ⇩</option>
                        <option value="price_asc">price ⇧</option>
                        <option value="price_desc">price ⇩</option>
                        <option value="artist">artist</option>
                        <option value="recent">most recent</option>
                    </select>
                </div>
            </div>
        </div>
        <div
            v-if="isDefaultSearch"
            class="callout callout-1-2"
        >
            <div class="callout-body">
                <p>
                    Use this page to search the entire collection by size price
                    and more
                </p>
            </div>
            <div
                class="callout-body callout-image"
                style="background-image: url(/search.jpg)"
            />
        </div>
        <div v-else class="artworks">
            <transition-group name="favorite">
                <Artwork
                    v-for="artwork in artworks"
                    :key="artwork.nid"
                    :artwork="artwork"
                    :show-artist="true"
                    @click.capture.prevent=""
                    @click="selectedArtwork = artwork"
                />
            </transition-group>
        </div>
        <transition name="modal">
            <modal v-if="selectedArtwork" @close="selectedArtwork = null">
                <template v-slot:body>
                    <Artwork
                        :key="selectedArtwork.nid"
                        :artwork="selectedArtwork"
                        :teaser="false"
                        :show-artist="true"
                    />
                    <a
                        v-if="previousArtwork"
                        @click="selectedArtwork = previousArtwork"
                        class="prev"
                        >Prev</a
                    >
                    <a
                        v-if="nextArtwork"
                        @click="selectedArtwork = nextArtwork"
                        class="next"
                        >Next</a
                    >
                    <div style="display: none" class="preloader">
                        <img
                            :src="previousArtwork.fullImage"
                            v-if="previousArtwork"
                        />
                        <img :src="nextArtwork.fullImage" v-if="nextArtwork" />
                    </div>
                </template>
            </modal>
        </transition>
    </div>
</template>

<script>
import ArtworkHelper from "@/mixins/PageHelper.js";
//import Choices from 'choices.js';
import VueMultiselect from "vue-multiselect";

function filterMixin(uniqName, valueCb, maxName, filterName, modelName) {
    return {
        computed: {
            [uniqName]() {
                const vm = this;
                return vm.allArtworks
                    .map((artwork) => vm[valueCb](artwork))
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .sort((a, b) => a - b);
            },
            [maxName]() {
                const vm = this;
                return vm[uniqName].length
                    ? vm[uniqName].reduce((a, v) => (v > a ? v : a))
                    : 0;
            },
            [filterName]() {
                // console.log(filterName, "dynamic generated function");
                const vm = this;
                const [min, max] = vm.searches[modelName];
                if (min == 0 && max == vm[maxName]) return vm.allArtworks;
                return vm.allArtworks.filter((artwork) => {
                    const h = vm[valueCb](artwork);
                    return h >= min && h <= max;
                });
            },
        },
    };
}

export default {
    name: "Search",
    components: { VueMultiselect },
    mixins: [
        ArtworkHelper,
        filterMixin(
            "uniqHeights",
            "artworkHeightInch",
            "heightRangeMax",
            "filteredByHeight",
            "heightRange"
        ),
        filterMixin(
            "uniqWidths",
            "artworkWidthInch",
            "widthRangeMax",
            "filteredByWidth",
            "widthRange"
        ),
        filterMixin(
            "uniqPrices",
            "artworkPrice",
            "priceRangeMax",
            "filteredByPrice",
            "priceRange"
        ),
    ],
    props: {
        initialSearch: {
            type: Object
        },
    },
    watch: {
        'initialSearch.keyword': {
            handler(newValue) {
                const vm = this;
                if (typeof newValue === 'undefined') newValue = '';
                vm.searches.keyword = newValue.toString();
            },
            immediate: true
        },
        'initialSearch.sortBy': {
            handler(newValue) {
                const vm = this;
                if (typeof newValue === 'undefined') newValue = '';
                vm.searches.sortBy = newValue.toString();
            },
            immediate: true
        },
    },
    data() {
        return {
            selectedArtwork: null,
        };
    },
    computed: {
        documentTitle() {
            return "Search | " + this.documentTitleBase;
        },
        resultCount() {
            return +this.filteredArtworks.length;
        },
        filteredByArtist() {
            //console.log("filteredByArtist");
            const vm = this;
            const ids = vm.searches.artist;
            if (ids.length === 0) return vm.allArtworks;
            return vm.allArtworks.filter((artwork) =>
                ids.includes(artwork.uid)
            );
        },
        filteredByKeyword() {
            const vm = this;
            //console.log("filteredByKeyword");
            if (!vm.searches.keyword) return vm.allArtworks;
            const search = vm.searches.keyword.toLowerCase();
            return vm.allArtworks.filter((artwork) => {
                if (typeof artwork._search_text == "undefined") {
                    const temp = document.createElement("div");
                    temp.innerHTML = artwork.body;
                    const sanitizedBody = temp.textContent || temp.innerText;
                    artwork._search_text = [
                        artwork.title,
                        sanitizedBody,
                        artwork.medium,
                        artwork.name,
                        artwork.availability,
                        artwork.taxonomy.length
                            ? artwork.taxonomy.map((t) => t.name).join(" ")
                            : "",
                    ]
                        .join(" ")
                        .toLowerCase();
                }

                return artwork._search_text.includes(search);
            });
        },
        filteredArtworks() {
            const vm = this;
            //console.log("filteredArtworks");
            var artworks;
            const filters = [
                vm.filteredByArtist,
                vm.filteredByKeyword,
                vm.filteredByHeight,
                vm.filteredByWidth,
                vm.filteredByPrice,
            ];
            filters.forEach((filter) => {
                if (!artworks) {
                    artworks = filter.filter(() => true);
                    return;
                }
                if (filter !== vm.allArtworks){
                    artworks = artworks.filter((artwork) =>
                        filter.includes(artwork)
                    );
                }
            });
            return artworks;
        },
    },
    //   methods: {
    //       clearSearch() {
    //           console.log('clearSearch')
    //       }
    //   }
};
</script>

<style lang="scss">
</style>
